import React, { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import {
  ReferralPageWrapper, DentistSection, PatientSection, FormFooter, FormSection,
  SuccessScreen, RadioGroup, Fw, FieldError, icls,
  DENTIST_DEFAULTS, PATIENT_DEFAULTS, FOOTER_DEFAULTS,
  validateCommon, submitReferral,
} from './ReferralBase';

const IMPLANT_SCOPE_OPTIONS = [
  { value: 'single', label: 'Single implant' },
  { value: 'multiple', label: 'Multiple implants' },
  { value: 'full_arch', label: 'Full arch / All-on-X' },
  { value: 'assessment', label: 'Assessment only' },
];

const BONE_QUALITY_OPTIONS = [
  { value: 'adequate', label: 'Appears adequate' },
  { value: 'borderline', label: 'Borderline / unsure' },
  { value: 'augmentation', label: 'Likely needs augmentation' },
];

const PROSTHETIC_OPTIONS = [
  { value: 'crown', label: 'Crown' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'overdenture', label: 'Overdenture' },
  { value: 'full_arch', label: 'Fixed full arch' },
  { value: 'undecided', label: 'Undecided / advise' },
];

const DEFAULTS = {
  ...DENTIST_DEFAULTS, ...PATIENT_DEFAULTS,
  toothSites: '', medicalHistory: '',
  implantScope: '', boneQuality: '', prostheticPlan: '',
  existingProsthetics: '', radiographsAvailable: '',
  specialInstructions: '',
  ...FOOTER_DEFAULTS,
};

function validate(form) {
  const errors = validateCommon(form);
  if (!form.toothSites.trim()) errors.toothSites = 'Required';
  if (!form.implantScope) errors.implantScope = 'Please select a scope';
  return errors;
}

export default function ImplantsReferral() {
  const [form, setForm] = useState(DEFAULTS);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try { await submitReferral(form, 'Dental Implants'); } catch (_) {}
    setSubmitting(false);
    setSubmitted(true);
  };

  const reset = () => { setForm(DEFAULTS); setErrors({}); setSubmitted(false); };
  const inputCls = (f) => icls(f, errors, focused);
  const commonProps = { form, errors, focused, onChange: handleChange, onFocus: setFocused, onBlur: () => setFocused(null) };

  return (
    <ReferralPageWrapper
      title="Dental Implants Referral"
      subtitle="Refer your patient for implant placement, full-arch restoration or implant planning consultation."
      photo="/images/referral/referral-implants.jpg">
      <h2 className="font-serif text-forest text-2xl font-semibold mb-1">Dental Implants Referral Form</h2>
      <p className="text-gray-500 text-sm mb-7">All fields marked <span className="text-red-400">*</span> are required.</p>

      <AnimatePresence mode="wait">
        {submitted ? (
          <SuccessScreen key="success" patientName={form.patientName} onReset={reset} />
        ) : (
          <form key="form" onSubmit={handleSubmit} noValidate>
            <DentistSection {...commonProps} />
            <PatientSection {...commonProps} />

            <FormSection title="Clinical Information">
              <div className="grid gap-5">
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="toothSites">
                    Tooth / Site Numbers <span className="text-red-400">*</span>
                  </label>
                  <input id="toothSites" name="toothSites" type="text" value={form.toothSites}
                    onChange={handleChange} placeholder="e.g. UL1, LR6"
                    onFocus={() => setFocused('toothSites')} onBlur={() => setFocused(null)}
                    className={inputCls('toothSites')} />
                  <FieldError error={errors.toothSites} />
                </Fw>
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="medicalHistory">
                    Relevant Medical History
                  </label>
                  <textarea id="medicalHistory" name="medicalHistory" rows={3} value={form.medicalHistory}
                    onChange={handleChange} placeholder="Medications, medical conditions, smoking status, bisphosphonate use…"
                    onFocus={() => setFocused('medicalHistory')} onBlur={() => setFocused(null)}
                    className={`${inputCls('medicalHistory')} resize-none`} />
                </Fw>
                <RadioGroup name="implantScope" label="Scope of Treatment" required
                  options={IMPLANT_SCOPE_OPTIONS} value={form.implantScope}
                  onChange={handleChange} error={errors.implantScope} />
                <RadioGroup name="boneQuality" label="Bone Quality Assessment"
                  options={BONE_QUALITY_OPTIONS} value={form.boneQuality}
                  onChange={handleChange} error={errors.boneQuality} />
                <RadioGroup name="prostheticPlan" label="Planned Prosthetic Option"
                  options={PROSTHETIC_OPTIONS} value={form.prostheticPlan}
                  onChange={handleChange} error={errors.prostheticPlan} />
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="existingProsthetics">
                    Existing Dentures / Prosthetics
                  </label>
                  <input id="existingProsthetics" name="existingProsthetics" type="text" value={form.existingProsthetics}
                    onChange={handleChange} placeholder="e.g. Full upper denture, RPD lower"
                    onFocus={() => setFocused('existingProsthetics')} onBlur={() => setFocused(null)}
                    className={inputCls('existingProsthetics')} />
                </Fw>
                <RadioGroup name="radiographsAvailable" label="Radiographs Available"
                  options={[{ value: 'yes', label: 'Yes — will attach / forward' }, { value: 'no', label: 'No' }]}
                  value={form.radiographsAvailable} onChange={handleChange} error={errors.radiographsAvailable} />
              </div>
            </FormSection>

            <FormSection title="Additional Information">
              <Fw>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="specialInstructions">
                  Special Instructions
                </label>
                <textarea id="specialInstructions" name="specialInstructions" rows={3} value={form.specialInstructions}
                  onChange={handleChange} placeholder="Any other relevant information…"
                  onFocus={() => setFocused('specialInstructions')} onBlur={() => setFocused(null)}
                  className={`${inputCls('specialInstructions')} resize-none`} />
              </Fw>
            </FormSection>

            <FormFooter {...commonProps} submitting={submitting} />
          </form>
        )}
      </AnimatePresence>
    </ReferralPageWrapper>
  );
}
